import React, { FC, useMemo } from 'react';
import { Chip, Field, Select, TextInput } from '@kontentino/ui';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { useCurrencies } from 'modules/listings/listingsSelectors';
import { useTranslation } from 'react-i18next';
import NumberUtil from 'utils/number';

type Props = {
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const MAX_ALLOWED_BUDGET_VALUE_LENGTH = 10;

const PostComposerBoostBudget: FC<Props> = ({ accordionProps }) => {
  const { form, setForm } = usePostComposer();
  const currencies = useCurrencies();
  const { t } = useTranslation();

  const currencyOptions = Object.entries(currencies.list).map(([key, val]) => ({
    value: key,
    label: val,
  }));

  return useMemo(
    () => (
      <AccordionWidget
        title={<AccordionWidgetTitle variant="boostBudget" />}
        titleSuffix={(isOpen) =>
          !isOpen &&
          form.budget && (
            <Chip variant="darkGrey" className="tw-px-2">
              {NumberUtil.compact(Number(form.budget))}{' '}
              {currencies.list[form.budgetCurrency]}
            </Chip>
          )
        }
        icon={<AccordionWidgetIcon variant="boostBudget" />}
        data-name="posts-composer_boost-budget"
        {...accordionProps}
      >
        <div className="tw-flex">
          <Field.Group className="tw-flex tw-w-full !tw-flex-row tw-items-center tw-gap-2">
            <div className="tw-flex-1">
              <UserPermissionGate
                scopes={UserPermission.POST_EDIT_BUDGET}
                deniedProps={{ disabled: true }}
              >
                <TextInput
                  type="number"
                  placeholder={t('placeholder.setBudget')}
                  value={form.budget ? form.budget : ''}
                  onChange={(event) => {
                    if (
                      event.currentTarget.value.length <=
                      MAX_ALLOWED_BUDGET_VALUE_LENGTH
                    ) {
                      setForm({ budget: event.currentTarget.value });
                    }
                  }}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key.length === 1 && event.key.match(/[^\d.]/g))
                      event.preventDefault();
                  }}
                  min={0}
                  data-cy="posts-composer_boost-budget-input"
                />
              </UserPermissionGate>
            </div>
            <UserPermissionGate
              scopes={UserPermission.POST_EDIT_BUDGET}
              deniedProps={{ isDisabled: true }}
            >
              <Select
                placeholder=""
                className="tw-w-20"
                styles={{
                  valueContainer: (styles) => ({
                    ...styles,
                    paddingRight: 0,
                  }),
                }}
                options={currencyOptions}
                onChange={(selectedOption) => {
                  setForm({
                    budgetCurrency: selectedOption
                      ? Number(selectedOption.value)
                      : undefined,
                  });
                }}
                value={
                  currencyOptions.find(
                    (option) => option.value === String(form.budgetCurrency),
                  ) || undefined
                }
              />
            </UserPermissionGate>
          </Field.Group>
        </div>
      </AccordionWidget>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.budget, form.budgetCurrency, accordionProps],
  );
};

export default PostComposerBoostBudget;
